import { getCookie } from "../utils/cookies";
import {
  readCachedConfig,
  type ApplicationConfig,
  type FeatureFlagsMap
} from "../application-config";
import { FeatureFlag, type FeatureFlagId } from "./featureFlags";

export type FeatureFlagLookupOptions = {
  /** Defaults to `window.location.search`. */
  search?: string;
  /** Defaults to `getCookie`. */
  readCookie?: (name: string) => string | null | undefined;
};

const TRUTHY = new Set(["true", "1", "yes"]);
const FALSY = new Set(["false", "0", "no"]);

/**
 * Interprets cookie / query / config flag values.
 * Empty string (e.g. `?AM-6728`) is treated as on.
 * Unrecognized values are ignored so lookup can fall through.
 */
export function parseFeatureFlagValue(value: unknown): boolean | undefined {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (value == null) return undefined;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "") return true;
  if (TRUTHY.has(normalized)) return true;
  if (FALSY.has(normalized)) return false;
  return undefined;
}

function getActiveSearch(search?: string): string {
  if (search !== undefined) return search;
  if (typeof window === "undefined") return "";
  return window.location.search;
}

function readQueryFlagValue(
  flag: FeatureFlagId,
  search?: string
): boolean | undefined {
  const params = new URLSearchParams(getActiveSearch(search));
  if (!params.has(flag)) return undefined;
  return parseFeatureFlagValue(params.get(flag));
}

function readCookieFlagValue(
  flag: FeatureFlagId,
  readCookie: FeatureFlagLookupOptions["readCookie"] = getCookie
): boolean | undefined {
  return parseFeatureFlagValue(readCookie(flag));
}

export function getFeatureFlags(
  config: ApplicationConfig | null | undefined
): FeatureFlagsMap {
  return config?.featureFlags ?? {};
}

/**
 * Resolves a flag from, in order:
 * 1. Active URL query param (`?AM-6728=true`)
 * 2. Cookie (`AM-6728=true`)
 * 3. application-config `featureFlags`
 *
 * An explicit query/cookie value wins over config (including `false` to force off).
 */
export function isFeatureEnabled(
  config: ApplicationConfig | null | undefined,
  flag: FeatureFlagId,
  options: FeatureFlagLookupOptions = {}
): boolean {
  const fromQuery = readQueryFlagValue(flag, options.search);
  if (fromQuery !== undefined) return fromQuery;

  const fromCookie = readCookieFlagValue(flag, options.readCookie);
  if (fromCookie !== undefined) return fromCookie;

  return getFeatureFlags(config)[flag] === true;
}

/** Config flags with cookie / query overrides applied for registered ids. */
export function getResolvedFeatureFlags(
  config: ApplicationConfig | null | undefined,
  options: FeatureFlagLookupOptions = {}
): FeatureFlagsMap {
  const resolved: FeatureFlagsMap = { ...getFeatureFlags(config) };
  (Object.values(FeatureFlag) as FeatureFlagId[]).forEach((flag) => {
    resolved[flag] = isFeatureEnabled(config, flag, options);
  });
  return resolved;
}

/** Reads session-cached application-config, then cookie / query overrides. */
export function isCachedFeatureEnabled(
  flag: FeatureFlagId,
  options: FeatureFlagLookupOptions = {}
): boolean {
  return isFeatureEnabled(readCachedConfig(), flag, options);
}
